// Corporations - shared specifications between client and server
// See docs/corp.md (design) and docs/mechanics/corporations.md (implementation).
//
// A corp is a "legal person" (persona jurídica): it owns property and holds
// credits, distinct from any individual player. Phase 1 only — see corp.md.

// ---------------------------------------------------------------------------
// Ownership generalization
// ---------------------------------------------------------------------------
// Private stations and in-planet buildings are owned by an "owning entity",
// which is either a player or a corp. Matches the `drones.owner_type` pattern.
export type OwnerType = 'player' | 'corp';

// ---------------------------------------------------------------------------
// Permissions — discrete capability flags (bitmask), NOT named roles.
// The manager is the super-user (holds all by definition) and is never stored
// in the permission table; it is identified by corporations.manager_player_id.
// Every actionable thing has a permission (corp.md §1.8).
// ---------------------------------------------------------------------------
export enum CorpPermission {
  VIEW_BANK         = 1 << 0, // View bank balance
  WITHDRAW_BANK     = 1 << 1, // Withdraw from bank
  VIEW_AUDIT        = 1 << 2, // View audit log
  COMMAND_DRONES    = 1 << 3, // Command drones of corp-owned stations
  PURCHASE_ASSETS   = 1 << 4, // Purchase assets / spend corp funds
  CONTROL_BUILDINGS = 1 << 5, // Control in-planet buildings
  MANAGE_CORP       = 1 << 6, // Assign/revoke permissions, corp settings
  MANAGE_MEMBERS    = 1 << 7, // Accept/reject applications, kick members (Phase 2)
}

// All Phase-1 permissions, for UI iteration.
export const CORP_PERMISSION_LIST: { flag: CorpPermission; key: string; label: string }[] = [
  { flag: CorpPermission.VIEW_BANK,         key: 'VIEW_BANK',         label: 'View bank balance' },
  { flag: CorpPermission.WITHDRAW_BANK,     key: 'WITHDRAW_BANK',     label: 'Withdraw from bank' },
  { flag: CorpPermission.VIEW_AUDIT,        key: 'VIEW_AUDIT',        label: 'View audit log' },
  { flag: CorpPermission.COMMAND_DRONES,    key: 'COMMAND_DRONES',    label: 'Command drones' },
  { flag: CorpPermission.PURCHASE_ASSETS,   key: 'PURCHASE_ASSETS',   label: 'Purchase assets / spend funds' },
  { flag: CorpPermission.CONTROL_BUILDINGS, key: 'CONTROL_BUILDINGS', label: 'Control in-planet buildings' },
  { flag: CorpPermission.MANAGE_CORP,       key: 'MANAGE_CORP',       label: 'Manage corp' },
  { flag: CorpPermission.MANAGE_MEMBERS,    key: 'MANAGE_MEMBERS',    label: 'Manage members' },
];

/** True if the given flag set includes the permission. Managers bypass this check entirely. */
export function hasPermission(flags: number, perm: CorpPermission): boolean {
  return (flags & perm) === perm;
}

// ---------------------------------------------------------------------------
// Identity constraints (corp.md §1.1)
// ---------------------------------------------------------------------------
export const CORP_TAG_MIN_LEN = 1;
export const CORP_TAG_MAX_LEN = 4; // variable length; raising this keeps shorter tags valid
export const CORP_NAME_MIN_LEN = 3;
export const CORP_NAME_MAX_LEN = 32;

const CORP_TAG_RE = /^[A-Za-z0-9]+$/;

/** Validate a tag (ticker). Returns null if valid, else a reason string. */
export function validateCorpTag(tag: string): string | null {
  if (tag.length < CORP_TAG_MIN_LEN || tag.length > CORP_TAG_MAX_LEN) {
    return `Tag must be ${CORP_TAG_MIN_LEN}-${CORP_TAG_MAX_LEN} characters`;
  }
  if (!CORP_TAG_RE.test(tag)) return 'Tag must be alphanumeric';
  return null;
}

/** Validate a name. Returns null if valid, else a reason string. */
export function validateCorpName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length < CORP_NAME_MIN_LEN || trimmed.length > CORP_NAME_MAX_LEN) {
    return `Name must be ${CORP_NAME_MIN_LEN}-${CORP_NAME_MAX_LEN} characters`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Creation contract (corp.md §1.4)
// ---------------------------------------------------------------------------
export const CORP_CONTRACT_EXPIRY_HOURS = 24;
export const CORP_CONTRACT_EXPIRY_MS = CORP_CONTRACT_EXPIRY_HOURS * 60 * 60 * 1000;
// How often the server sweeps for expired contracts.
export const CORP_CONTRACT_SWEEP_INTERVAL_MS = 10 * 60 * 1000;

export type ContractStatus = 'pending' | 'cancelled' | 'expired' | 'finalized';
export type ContractDecision = 'pending' | 'accepted' | 'rejected';

/** A declared owner line at corp creation: a player and their ownership percentage. */
export interface OwnerDeclaration {
  playerId: string;
  percentage: number; // 0 < pct <= 100
}

/**
 * Percentages must sum to exactly 100 (corp.md §1.2). Compares with a small
 * epsilon to tolerate NUMERIC(5,2) rounding.
 */
export function ownerPercentagesValid(owners: { percentage: number }[]): boolean {
  if (owners.length === 0) return false;
  const sum = owners.reduce((acc, o) => acc + o.percentage, 0);
  if (owners.some((o) => o.percentage <= 0 || o.percentage > 100)) return false;
  return Math.abs(sum - 100) < 0.01;
}

/**
 * Percentage determines contribution, computed from total capital (percent-first
 * convention, corp.md §1.2). Uses floor; rounding remainder stays with capital.
 */
export function contributionFor(totalCapital: number, percentage: number): number {
  return Math.floor((totalCapital * percentage) / 100);
}

// ---------------------------------------------------------------------------
// Asset transfer (corp.md §1.7) — pure transfer (gift); fee is a symbolic sink.
// ---------------------------------------------------------------------------
export type TransferableAssetType = 'private_station' | 'planet_structure';
export type TransferOfferStatus = 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled';

// Symbolic credit sink, NOT a price. Keep at 0 in Phase 1 (uncalibratable yet).
export const CORP_TRANSFER_FEE = 0;
export const CORP_TRANSFER_OFFER_EXPIRY_MS = CORP_CONTRACT_EXPIRY_MS;

// ---------------------------------------------------------------------------
// Audit log (corp.md §1.9)
// ---------------------------------------------------------------------------
export type CorpAuditAction =
  | 'corp_created'
  | 'capital_debit'
  | 'bank_withdraw'
  | 'permission_change'
  | 'asset_purchase'
  | 'asset_sale'
  | 'transfer_in'
  | 'transfer_out';

// ---------------------------------------------------------------------------
// Notifications (generic — serves contracts and transfer offers)
// ---------------------------------------------------------------------------
export type NotificationType = 'corp_contract' | 'transfer_offer' | 'corp_application' | 'drone_levelup';
export type NotificationStatus = 'unread' | 'read' | 'actioned';

// ---------------------------------------------------------------------------
// PHASE 2 — membership, distributions, voting (corp.md §2). All tunable.
// ---------------------------------------------------------------------------
export const CORP_DAY_MS = 24 * 60 * 60 * 1000; // 1 game-day = 1 real-day

// Distributions (corp.md §2.3)
export const DISTRIBUTION_TICK_INTERVAL_MS = 60 * 60 * 1000; // hourly sweep
export const DISTRIBUTION_DEFAULT_PERIOD_DAYS = 7;

// Manager-removal voting (corp.md §2.4) — anti-spam knobs, calibrate with real corps.
export const VOTE_MIN_TRIGGER_PCT = 10;   // owner must hold ≥10% to start a vote
export const VOTE_PASS_PCT = 51;          // 51% of TOTAL ownership (non-voters = "keep")
export const VOTE_COOLDOWN_DAYS = 3;      // no new vote for X days after one fails
export const VOTE_DURATION_DAYS = 3;      // a vote that doesn't reach 51% expires as failed
export const VOTE_SWEEP_INTERVAL_MS = 60 * 60 * 1000;

export type CorpApplicationStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn';
export type ManagerVoteStatus = 'pending' | 'passed' | 'failed';
export type VoteDecision = 'remove' | 'keep';

export interface CorpMemberDTO {
  playerId: string;
  playerName: string;
}

export interface CorpApplicationDTO {
  id: string;
  corpId: string;
  playerId: string;
  playerName: string;
  status: CorpApplicationStatus;
  createdAt: number;
}

export interface DistributionConfig {
  percentage: number;       // 0..100 of bank paid out each period
  periodDays: number;       // game-days between payouts
  lastDistributionAt: number | null;
}

export interface ManagerVoteDTO {
  id: string;
  corpId: string;
  initiatedBy: string;
  nomineePlayerId: string;
  nomineeName: string;
  status: ManagerVoteStatus;
  expiresAt: number;
  // Tally (weighted by ownership %)
  removePct: number;
  myBallot: VoteDecision | null;
}

// ---------------------------------------------------------------------------
// DTOs (client <-> server)
// ---------------------------------------------------------------------------
export interface CorpDTO {
  id: string;
  name: string;
  tag: string;
  managerPlayerId: string;
  bankBalance: number;
  createdAt: number;
}

export interface CorpOwnerDTO {
  playerId: string;
  playerName: string;
  percentage: number;
}

export interface CorpMemberPermissionDTO {
  playerId: string;
  playerName: string;
  permissionFlags: number;
}

export interface CreationContractDTO {
  id: string;
  name: string;
  tag: string;
  founderPlayerId: string;
  founderName: string;
  managerPlayerId: string;
  totalCapital: number;
  status: ContractStatus;
  expiresAt: number;
  owners: {
    playerId: string;
    playerName: string;
    percentage: number;
    contribution: number;
    decision: ContractDecision;
  }[];
}

export interface AssetTransferOfferDTO {
  id: string;
  assetType: TransferableAssetType;
  assetId: string;
  assetName: string;
  fromName: string;
  toOwnerType: OwnerType;
  toOwnerId: string;
  status: TransferOfferStatus;
  expiresAt: number;
}

export interface NotificationDTO {
  id: string;
  type: NotificationType;
  payload: Record<string, unknown>;
  status: NotificationStatus;
  createdAt: number;
}

export interface CorpAuditEntryDTO {
  id: string;
  action: CorpAuditAction;
  actorName: string | null;
  detail: Record<string, unknown>;
  createdAt: number;
}
